
/*
*   File : fund-order.js
*   Author : https://evoqins.com
*   Description : Fund order detail
*   Version : 1.0.0
*/

// import packages
import { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// import components
import { Header } from "../../Components/Header";
import { Breadcrumb, Loader } from "../../Components/Other";
import { Icon } from "../../Components/Icon";

// import services
import APIService from "../../Services/api-service";
import { _getOrderStatus, _getTransactionStatus } from "../../Helper/helper";


const FundOrders = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [screenLoader, setScreenLoader] = useState(true);
    const [fund, setFund] = useState([]);
    const [orderDetail, setOrderDetail] = useState({});


    useEffect(() => {
        if (location.state !== null) {
            _getOrderDetails(location.state.id);
        }
        // eslint-disable-next-line
    }, [location])

    useEffect(() => {
        // Add the event listener
        window.addEventListener('popstate', _handleCurrentNavigate);

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('popstate', _handleCurrentNavigate);
        };
        //eslint-disable-next-line
    }, []);

    // navigate to home screen
    function _handleParentNavigate() {
        navigate("/");
    }

    // navigate to parent screen
    function _handleCurrentNavigate() {
        navigate("/orders", {
            state: {
                type: location.state.type,
                paymentTypes: location.state.paymentTypes,
                orderTypes: location.state.orderTypes,
                orderStatus: location.state.orderStatus,
                startDate: location.state.startDate,
                endDate: location.state.endDate,
                page: location.state.page
            }
        });
    }

    // API - order details
    const _getOrderDetails = (id) => {
        const url = "/order/detail";
        const request = {
            order_id: id
        }

        APIService(true, url, request).then((response) => {
            if (response.status_code === 200) {
                setOrderDetail(response.data.summary);
                setFund(response.data.funds);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
                navigate("/orders")
            }
            setScreenLoader(false);
        });
    }

    return (
        screenLoader === true ?
            <Loader height="h-80vh" />
            :
            <div className="bg-white border-radius-tl-40px border-radius-bl-40px">
                <Header type={2} />
                <div className="row ">
                    <div className="col-lg-11 padding-44px-bottom">
                        <section className="px-sm-4 px-3">
                            <Breadcrumb mainPage="Home"
                                currentPage="Orders"
                                type={2}
                                childPage={orderDetail.name}
                                handleParentNavigate={_handleParentNavigate}
                                handleCurrentNavigate={_handleCurrentNavigate} />

                            <Icon icon="Arrow-Right"
                                size={32}
                                className="mb-3 margin-32px-top e-transform-left-arrow cursor-pointer"
                                onClick={_handleCurrentNavigate} />
                            <div className="row row-gap-16px">
                                <div className="col-xl-8 col-lg-10 order-lg-1 order-2">
                                    <div className="box-shadow-black border-radius-24px pt-sm-4 pt-3">

                                        <div className="pb-sm-4 pb-3 px-sm-4 px-3 border-bottom-1px border-chinese-white">
                                            {orderDetail.is_external === true && <div className="d-flex align-items-center gap-2px color-bright-periwinkle e-font-14 line-height-20px e-montserrat-medium mb-2">
                                                <Icon icon="external" size={18}
                                                /> External
                                            </div>}
                                            <div className="d-flex align-items-start gap-8px">
                                                <img src={orderDetail.icon}
                                                    width={48}
                                                    height={48}
                                                    alt={orderDetail.scheme_name}
                                                    draggable={false}
                                                    className="border-radius-12px e-fund-detail-chart object-fit-contain d-sm-block d-none" />
                                                <img src={orderDetail.icon}
                                                    width={38}
                                                    height={38}
                                                    alt={orderDetail.scheme_name}
                                                    draggable={false}
                                                    className="border-radius-12px e-fund-detail-chart object-fit-contain d-sm-none d-block" />
                                                <div className="d-flex gap-8px flex-column">
                                                    <p className="color-eerie-black e-font-16 e-line-height-20 e-montserrat-semi-bold mb-0 e-row-title">{orderDetail.name}</p>
                                                    <div className="d-flex align-items-center color-outer-space e-font-12 e-line-height-16 e-montserrat-regular">
                                                        <span className="e-bg-lavender-frost padding-6px-tb px-2 color-rebecca-purple e-font-12 e-line-height-14 e-montserrat-medium border-radius-30px me-3">{orderDetail.order_no}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="pt-sm-3 pt-2 pb-sm-4 pb-2 mx-sm-4 mx-3 border-bottom-1px border-chinese-white">
                                            <h6 className="e-montserrat-semi-bold e-font-16 e-line-height-30 color-eerie-black mb-md-3 mb-2">Order Details</h6>
                                            {
                                                (orderDetail.type === "Switch" || orderDetail.type === "STP") ?
                                                    <Fragment>
                                                        <div className="row">
                                                            <div className="col-sm-4 col-6 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Date</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{orderDetail.order_date}</p>
                                                            </div>

                                                            {
                                                                fund.frequency &&
                                                                <div className="col-sm-4 col-6 pb-md-4 pb-2">
                                                                    <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Frequency</p>
                                                                    <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.frequency_text}</p>
                                                                </div>
                                                            }

                                                            <div className="col-sm-4 col-6 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Order Type</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{orderDetail.type}</p>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-12 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Transfer Out</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.switch_out.name}</p>
                                                            </div>
                                                            <div className="col-sm-3 col-4 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Units</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.switch_out.units == null ? '-' : fund.switch_out.units}</p>
                                                            </div>
                                                            <div className="col-sm-3 col-4 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">NAV</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.switch_out.nav == null ? '-' : fund.switch_out.nav}</p>
                                                            </div>

                                                        </div>
                                                        <img src={require("../../Assets/Images/Orders/switch.svg").default}
                                                            alt="switch"
                                                            className=" mb-3" />
                                                        <div className="row">
                                                            <div className="col-12 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Transfer In</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.switch_in.name}</p>
                                                            </div>
                                                            <div className="col-sm-3 col-4 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Units</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.switch_in.units == null ? '-' : fund.switch_in.units}</p>
                                                            </div>
                                                            <div className="col-sm-3 col-4 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">NAV</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.switch_in.nav == null ? '-' : fund.switch_in.nav}</p>
                                                            </div>

                                                        </div>
                                                        <div className="row">
                                                            <div className="col-sm-3 col-4 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-24 mb-1 color-outer-space">Amount</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.amount_text ? fund.amount_text : 0}</p>
                                                            </div>
                                                            <div className="col-sm-3 col-4 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-24 mb-1 color-outer-space">{"Allocated Date"}</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.units_allocated ? fund.units_allocated : "-"}</p>
                                                            </div>
                                                            <div className="col-sm-3 col-4 pb-md-4 pb-2">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-24 mb-1 color-outer-space">Transaction ID</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.transaction_id}</p>
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-3 col-4 pb-md-4 pb-2">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Folio No.</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.folio_no ? fund.folio_no : "-"}</p>
                                                        </div>
                                                        <div className="col-sm-4 col-6 ">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Order Status</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 ${_getOrderStatus(orderDetail.order_status).color}`}>
                                                                <img src={_getOrderStatus(orderDetail.order_status).icon}
                                                                    alt="Success"
                                                                    draggable={false}
                                                                    className="me-1" />
                                                                {orderDetail.order_status}</p>
                                                        </div>
                                                    </Fragment>
                                                    :
                                                    <div className="row">
                                                        <div className="col-sm-4 col-6 pb-md-4 pb-2">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Date</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{orderDetail.order_date}</p>
                                                        </div>

                                                        <div className="col-sm-4 col-6 pb-md-4 pb-2">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Amount</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{orderDetail.amount_text}</p>
                                                        </div>

                                                        <div className="col-sm-4 col-6 pb-md-4 pb-2">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Order Type</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{orderDetail.type}</p>
                                                        </div>

                                                        {
                                                            fund.frequency &&
                                                            <div className="col-sm-4 col-6 pb-0 mb-1">
                                                                <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Frequency</p>
                                                                <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.frequency_text}</p>
                                                            </div>
                                                        }

                                                        {(orderDetail.type !== "Withdraw" && orderDetail.type !== "SWP") && <div className="col-sm-4 col-6 pb-0 mb-1">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Payment Status</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 ${_getOrderStatus(orderDetail.payment_status).color}`}>
                                                                <img src={_getOrderStatus(orderDetail.payment_status).icon}
                                                                    alt="Success"
                                                                    draggable={false}
                                                                    className="me-1" />
                                                                {orderDetail.payment_status}
                                                            </p>
                                                        </div>}
                                                        <div className="col-sm-4 col-6 ">
                                                            <p className="e-montserrat-regular e-font-14 e-line-height-20 mb-1 color-outer-space">Order Status</p>
                                                            <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 ${_getOrderStatus(orderDetail.order_status).color}`}>
                                                                <img src={_getOrderStatus(orderDetail.order_status).icon}
                                                                    alt="Success"
                                                                    draggable={false}
                                                                    className="me-1" />
                                                                {orderDetail.order_status}</p>
                                                        </div>
                                                    </div>
                                            }
                                            {orderDetail.order_status === "Failed" && orderDetail.failure_reason && <div className="d-flex align-items-sm-center align-items-start padding-12px-all e-bg-misty-rose border-radius-12px mt-4 color-crimson-red e-font-12 e-line-height-20 gap-4px e-montserrat-medium">
                                                <span>
                                                    <Icon icon="info-circle-yellow"
                                                        size={16}
                                                        color="#d92d20"
                                                        className="min-w-16px" />
                                                </span>
                                                <span>{orderDetail.failure_reason}</span>
                                            </div>}
                                        </div>
                                        <div className="pb-sm-3 pb-2 mx-sm-4 mx-3">
                                            {
                                                (orderDetail.type !== "Switch" && orderDetail.type !== "STP") &&
                                                <div className="row">

                                                    <div className="col-sm-4 col-6 pt-sm-3 pt-2">
                                                        <p className="e-montserrat-regular e-font-14 e-line-height-24 mb-1 color-outer-space">Units</p>
                                                        <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.units ? fund.units : "-"}</p>
                                                    </div>
                                                    <div className="col-sm-4 col-6 pt-sm-3 pt-2">
                                                        <p className="e-montserrat-regular e-font-14 e-line-height-24 mb-1 color-outer-space">Transaction ID</p>
                                                        <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.transaction_id}</p>
                                                    </div>
                                                    <div className="col-sm-4 col-6 pt-sm-3 pt-2">
                                                        <p className="e-montserrat-regular e-font-14 e-line-height-24 mb-1 color-outer-space">NAV</p>
                                                        <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.nav ? fund.nav : "-"}</p>
                                                    </div>
                                                    <div className="col-sm-4 col-6 pt-sm-4 pt-2">
                                                        <p className="e-montserrat-regular e-font-14 e-line-height-24 mb-1 color-outer-space">{orderDetail.type === "Withdraw" || orderDetail.type === "SWP" ? "Traded Date" : "Allocated Date"}</p>
                                                        <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.units_allocated ? fund.units_allocated : "-"}</p>
                                                    </div>
                                                    <div className="col-sm-4 col-6 pt-sm-4 pt-2">
                                                        <p className="e-montserrat-regular e-font-14 e-line-height-24 mb-1 color-outer-space">Folio no</p>
                                                        <p className={`e-montserrat-semi-bold e-font-14 e-line-height-20 mb-0 color-eerie-black`}>{fund.folio_no ? fund.folio_no : "-"}</p>
                                                    </div>
                                                </div>
                                            }

                                            <p className="e-montserrat-semi-bold e-font-14 e-line-height-24 mb-3 color-eerie-black pt-4">Transaction Status</p>

                                            <div className="d-md-flex d-none">

                                                {
                                                    fund.timeline.map((item, index) => {
                                                        let step_data = _getTransactionStatus(item.status, index);
                                                        return (
                                                            <div className={`e-border gap-12px position-relative w-35 z-index-3 pe-1`} >
                                                                <div className={`e-progress-image d-flex align-items-center justify-content-start position-relative`} >
                                                                    <img src={step_data.icon} alt={"progress"}
                                                                        draggable={false}
                                                                        className="bg-white z-index-4" />
                                                                    {
                                                                        index !== fund.timeline.length - 1 && <div
                                                                            className="e-progress-line"
                                                                            style={{
                                                                                position: 'absolute',
                                                                                width: '100%',
                                                                                height: '1px',
                                                                                left: '1.25rem',
                                                                                zIndex: 1,
                                                                                background: step_data.bg_color
                                                                            }}
                                                                        />
                                                                    }
                                                                </div>


                                                                <div className="d-block padding-12px-top">
                                                                    <p className={`e-montserrat-medium e-font-14 e-line-height-20 mb-1 ${step_data.color}`}>
                                                                        {item.label}
                                                                    </p>
                                                                    <p className="color-dark-slate e-montserrat-medium e-font-12 e-line-height-20 mb-0">{item.value ? item.value : "-"}</p>
                                                                </div>

                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>

                                            <div className="d-md-none d-block">
                                                {
                                                    fund.timeline.map((item, index) => {
                                                        let step_data = _getTransactionStatus(item.status, index);
                                                        return (
                                                            <div className={`e-vertical-border gap-12px  d-flex align-items-start z-index-3 bg-white ${index > 0 ? 'pt-5' : ''}`}>
                                                                <div className={`e-progress-image d-flex align-items-start justify-content-start position-relative`}>
                                                                    <img src={step_data.icon} alt={"progress"}
                                                                        draggable={false}
                                                                        className="bg-white z-index-2"
                                                                    />
                                                                    {
                                                                        index !== fund.timeline.length - 1 && <div
                                                                            className="e-progress-line"
                                                                            style={{
                                                                                position: 'absolute',
                                                                                height: '80px',
                                                                                width: '1px',
                                                                                left: '0.625rem',
                                                                                top: "18px",
                                                                                zIndex: 1,
                                                                                background: step_data.bg_color
                                                                            }}
                                                                        />
                                                                    }
                                                                </div>

                                                                <div className="d-block">
                                                                    <p className={`e-montserrat-medium e-font-14 e-line-height-20 mb-1 ${step_data.color}`}>
                                                                        {item.label}
                                                                    </p>
                                                                    <p className="color-dark-slate e-montserrat-medium e-font-12 e-line-height-20 mb-0">{item.value ? item.value : "-"}</p>
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>

                                        </div>
                                    </div>

                                </div>

                            </div>
                        </section>
                    </div>

                </div >
            </div >
    )
}
export default FundOrders